import { api } from './client';

export type LeaveStatus = 'PENDING' | 'APPROVED' | 'REJECTED' | 'CANCELLED';

export interface LeaveRequestItem {
  id: string;
  leaveType: { id: string; type: string; displayName: string };
  startDate: string;
  endDate: string;
  totalDays: number;
  halfDay?: 'FULL' | 'FIRST_HALF' | 'SECOND_HALF' | null;
  status: LeaveStatus | string;
  reason: string | null;
  approvalDate: string | null;
  rejectionReason: string | null;
  createdAt: string;
  approvedBy?: { id: string; fullName: string } | null;
  employee?: { id: string; fullName: string; email: string; department: { name: string } };
}

export interface LeaveHistoryResponse {
  requests: LeaveRequestItem[];
  pagination: {
    page: number;
    limit: number;
    totalCount: number;
    totalPages: number;
    hasNextPage: boolean;
  };
}

export interface LeaveBalanceItem {
  leaveType: { id: string; type: string; displayName: string };
  year: number;
  allocatedDays?: number;
  usedDays: number;
  remainingDays: number;
  totalAvailable?: number;
}

export interface LeaveCalendarItem {
  id: string;
  startDate: string;
  endDate: string;
  name: string;
  leaveLabel?: string;
  isMine?: boolean;
}

/** Employee's own leave requests (always scoped to logged-in user). */
export function getLeaveHistory(params?: {
  status?: LeaveStatus | 'ALL';
  year?: number;
  page?: number;
  limit?: number;
}) {
  const search = new URLSearchParams();
  if (params?.status && params.status !== 'ALL') {
    search.set('status', params.status);
  }
  if (params?.year != null) search.set('year', String(params.year));
  if (params?.page != null) search.set('page', String(params.page));
  if (params?.limit != null) search.set('limit', String(params.limit));
  const q = search.toString();
  return api<LeaveHistoryResponse>(`/leave/history${q ? `?${q}` : ''}`);
}

/** Direct reports' leave requests (manager/HR scope). */
export function getTeamLeaveRequests(params?: { status?: LeaveStatus }) {
  const search = new URLSearchParams({ scope: 'team' });
  if (params?.status) search.set('status', params.status);
  return api<LeaveRequestItem[]>(`/leave?${search.toString()}`);
}

export function getLeaveCalendar(params?: { month?: number; year?: number }) {
  const search = new URLSearchParams();
  if (params?.month != null) search.set('month', String(params.month));
  if (params?.year != null) search.set('year', String(params.year));
  const q = search.toString();
  return api<LeaveCalendarItem[]>(`/leave/calendar${q ? `?${q}` : ''}`);
}

export function approveLeaveRequest(id: string) {
  return api<{ success: boolean; message: string }>(`/leave-requests/${id}/approve`, {
    method: 'POST',
    body: JSON.stringify({ action: 'approve' }),
  });
}

export function rejectLeaveRequest(id: string, rejectionReason: string) {
  return api<{ success: boolean; message: string }>(`/leave-requests/${id}/reject`, {
    method: 'POST',
    body: JSON.stringify({ rejectionReason }),
  });
}

export function cancelLeaveRequest(id: string) {
  return api<{ success: boolean; message: string }>(`/leave-requests/${id}/cancel`, {
    method: 'POST',
  });
}

export { canApproveTeam as canApproveLeave } from '@/lib/roles';

export function getLeaveBalances(employeeId: string, year?: number) {
  const q = year != null ? `?year=${year}` : '';
  return api<{ data: LeaveBalanceItem[] }>(`/employees/${employeeId}/leave-balances${q}`);
}

export type LeaveTypeOption = {
  id: string;
  type: string;
  displayName: string;
};

export function getLeaveTypes() {
  return api<LeaveTypeOption[]>('/leave/types');
}

export interface CreateLeaveResponse {
  message: string;
  leaveRequest: LeaveRequestItem;
}

export function createLeaveRequest(body: {
  leaveTypeId: string;
  startDate: string;
  endDate: string;
  halfDay?: 'FULL' | 'FIRST_HALF' | 'SECOND_HALF';
  reason?: string;
}) {
  return api<CreateLeaveResponse>('/leave-requests', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export interface PublicHoliday {
  date: string;
  name: string;
  description?: string | null;
  isSubstitute?: boolean;
  substituteDate?: string | null;
}

/** Public holidays for a month (state-aware: filtered for the employee's state). */
export function getPublicHolidays(month: number, year: number) {
  return api<PublicHoliday[]>(`/holidays?month=${month}&year=${year}`);
}

export {
  LEAVE_STATUS_LABELS,
  LEAVE_STATUS_BADGE_CLASS as LEAVE_STATUS_COLORS,
} from '@hrmydeck/design-tokens/status';
